import type { FC, ReactNode } from 'react'
import { useCallback, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import type { SxProps, Theme } from '@mui/material'
import { Box, Tooltip, Typography } from '@mui/material'
import { STATUS_ICON_SIZE_COLLAPSED } from '@/features/status'
import { standardTooltipProps } from '@/common/components/tooltipStyles'

interface SidebarNavigationItemProps {
  readonly icon: ReactNode
  readonly label: string
  readonly route: string
  readonly isCollapsed?: boolean
  readonly matchExact?: boolean
}

export const SidebarNavigationItem: FC<SidebarNavigationItemProps> = ({
  icon,
  label,
  route,
  isCollapsed = false,
  matchExact = false,
}) => {
  const navigate = useNavigate()
  const location = useLocation()

  const isActive = useMemo(() => {
    if (matchExact) {
      return location.pathname === route
    }
    return location.pathname.startsWith(route)
  }, [location.pathname, route, matchExact])

  const handleClick = useCallback(() => {
    navigate(route)
  }, [navigate, route])

  if (isCollapsed) {
    return (
      <Tooltip {...standardTooltipProps} title={label} placement="right">
        <Box
          component="button"
          onClick={handleClick}
          sx={navigationCollapsedButtonSx}
          aria-label={label}
          aria-current={isActive ? 'page' : undefined}
        >
          <Box sx={navigationCollapsedIconSx(isActive)}>{icon}</Box>
        </Box>
      </Tooltip>
    )
  }

  return (
    <Box
      component="button"
      onClick={handleClick}
      sx={navigationExpandedButtonSx(isActive)}
      aria-current={isActive ? 'page' : undefined}
    >
      <Box sx={navigationExpandedIconSx(isActive)}>{icon}</Box>
      <Typography variant="body2" sx={navigationLabelSx(isActive)}>
        {label}
      </Typography>
    </Box>
  )
}

const navigationCollapsedButtonSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  background: 'transparent',
  padding: '6px',
  borderRadius: 1,
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: 'action.hover',
    '& .MuiSvgIcon-root': {
      color: 'text.primary',
      opacity: 1,
    },
  },
}

const navigationCollapsedIconSx = (isActive: boolean): SxProps<Theme> => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& .MuiSvgIcon-root': {
    fontSize: STATUS_ICON_SIZE_COLLAPSED,
    color: isActive ? 'text.primary' : 'text.disabled',
    opacity: isActive ? 1 : 0.6,
    transition: 'color 200ms ease-in-out, opacity 200ms ease-in-out',
  },
})

const navigationExpandedButtonSx = (isActive: boolean): SxProps<Theme> => ({
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  width: '100%',
  padding: '8px 12px',
  border: 'none',
  borderRadius: 1,
  backgroundColor: 'transparent',
  cursor: isActive ? 'default' : 'pointer',
  transition: 'background-color 150ms ease-in-out',
  '&:hover': {
    backgroundColor: isActive ? 'transparent' : 'action.hover',
  },
})

const navigationExpandedIconSx = (isActive: boolean): SxProps<Theme> => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& .MuiSvgIcon-root': {
    fontSize: 20,
    color: isActive ? 'text.primary' : 'text.secondary',
    transition: 'color 150ms ease-in-out',
  },
})

const navigationLabelSx = (isActive: boolean): SxProps<Theme> => ({
  flex: 1,
  color: isActive ? 'text.primary' : 'text.secondary',
  fontSize: '0.875rem',
  textAlign: 'left',
  transition: 'color 150ms ease-in-out',
})
